/**
 * The notebook's extra matrix columns, shared by both matrix components:
 *
 *  - Med Z   (`med-z (info)`): the median over a row's peers of each pair's
 *    z-score, z = (current spread - mean of its history) / sample std. The
 *    diagonal has no z and is left out (the notebook's z diagonal is NaN),
 *    unlike Med%, where it counts as 50.
 *  - Cheap bp (`cheap-bp`): 100 x (today's yield - the ticker's mean historical
 *    yield). Positive means the yield is above its own average, i.e. cheaper.
 *  - Rank    (`rank`): 1 for the highest cheap-bp in the list, ties sharing the
 *    lowest rank (pandas rank(method='min', ascending=False)).
 *
 * Yield spreads (YTC / YTM views) take the pair's mean and std from the matrix
 * payload and the live spread from the price feed, so z moves with the market.
 * The $ view's z is computed on the server over the same synthetic series that
 * gives that view its percentile (`dollar_spread.z`), as fresh as that percentile.
 */

const finite = (v) => typeof v === 'number' && Number.isFinite(v);

/** z of one yield-spread cell field ({ current, mean, std }), or null. */
export function pairZ(field) {
  if (!field || !finite(field.current) || !finite(field.mean) || !finite(field.std) || !(field.std > 0)) return null;
  return (field.current - field.mean) / field.std;
}

/** z of one $ cell field (computed by the server), or null. */
export function dollarZ(field) {
  return field && finite(field.z) ? field.z : null;
}

/**
 * Median of a row's pair z-scores, skipping the diagonal and pairs without one.
 * A row with none has no Med Z (null) rather than a made-up 0.
 */
export function medianRowZ(ticker1, tickers, row, getZ) {
  if (!row) return null;
  const vals = [];
  for (const t of tickers) {
    if (t === ticker1) continue;
    const z = getZ(row[t]);
    if (finite(z)) vals.push(z);
  }
  if (vals.length === 0) return null;
  vals.sort((a, b) => a - b);
  const mid = Math.floor(vals.length / 2);
  return vals.length % 2 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2;
}

/** Yield the cheap-bp / rank columns are measured on for a view. */
export function yieldBasis(spreadType) {
  return spreadType === 'spot-yield' ? 'ytm' : 'ytc';
}

/** Today's yield minus the mean of its history, in bp; null if either is missing. */
export function cheapBp(liveYield, histMean) {
  if (!finite(liveYield) || liveYield === 0 || !finite(histMean)) return null;
  return 100 * (liveYield - histMean);
}

/**
 * cheap-bp and rank for every ticker of a list.
 *
 * @param {string[]} tickers
 * @param {Object} livePrices ticker -> { ytm, ytc } from the price feed
 * @param {Object} yields     ticker -> { ytm, ytc, hist: { mean_ytm, mean_ytc } } from the matrix payload
 * @param {'ytc'|'ytm'} basis
 * @returns {{ cheapBp: Object, rank: Object }} ticker -> number | null
 */
export function cheapAndRank(tickers, livePrices, yields, basis) {
  const meanKey = basis === 'ytm' ? 'mean_ytm' : 'mean_ytc';
  const cheap = {};
  const scored = [];
  for (const t of tickers) {
    // The feed's value moves with the price; the payload's is the one the matrix
    // was built with, used until the first tick arrives.
    const live = finite(livePrices?.[t]?.[basis]) && livePrices[t][basis] !== 0
      ? livePrices[t][basis]
      : yields?.[t]?.[basis];
    const v = cheapBp(live, yields?.[t]?.hist?.[meanKey]);
    cheap[t] = v;
    if (v !== null) scored.push(v);
  }
  scored.sort((a, b) => b - a);
  // Equal values share the lowest rank: the position of the first of them.
  const firstPosition = new Map();
  scored.forEach((v, i) => { if (!firstPosition.has(v)) firstPosition.set(v, i + 1); });
  const rank = {};
  for (const t of tickers) rank[t] = cheap[t] === null ? null : firstPosition.get(cheap[t]);
  return { cheapBp: cheap, rank };
}

/** Ascending/descending comparison with missing values always last. */
export function compareNullsLast(a, b, direction) {
  const an = a === null || a === undefined || Number.isNaN(a);
  const bn = b === null || b === undefined || Number.isNaN(b);
  if (an && bn) return 0;
  if (an) return 1;
  if (bn) return -1;
  return direction === 'asc' ? a - b : b - a;
}

// ---- display ---------------------------------------------------------------

export const formatZ = (z) => (z === null || z === undefined ? '-' : z.toFixed(2));
export const formatCheapBp = (bp) => (bp === null || bp === undefined ? '-' : bp.toFixed(1));
export const formatRank = (r) => (r === null || r === undefined ? '-' : String(r));

const BASIS_LABEL = { ytc: 'YTC', ytm: 'YTM' };

export function medZTitle(spreadType) {
  return spreadType === 'dollar' || spreadType === 'percentile'
    ? 'Median over peers of each pair\'s z-score: today\'s yield-adjusted dollar spread (row minus peer) against its history, in standard deviations. Positive = the row\'s price is above its usual gap to the peer (richer).'
    : 'Median over peers of each pair\'s z-score: today\'s yield spread (row minus peer) against its history, in standard deviations. Positive = the row\'s yield is above its usual gap to the peer (cheaper).';
}

export function cheapTitle(basis) {
  return `${BASIS_LABEL[basis] || basis} today minus its own average over the lookback, in bp. Positive = cheaper than usual.`;
}

export function rankTitle(basis) {
  return `Rank by Cheap bp on the ${BASIS_LABEL[basis] || basis} basis: 1 = cheapest in the list.`;
}

/** One-line history coverage for a ticker (the notebook's History Coverage sheet). */
export function coverageTitle(hist) {
  if (!hist || !hist.bars) return null;
  return `History: ${hist.bars} bars, ${hist.first} to ${hist.last} ET`;
}
